import { FC, ReactNode } from 'react';
import {
  Box,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router';

interface PageHeaderProps {
  title: string;
  icon?: ReactNode;
  backRoute?: string;
  subtitle?: string;
  /** When true the back button is hidden on md+ (desktop) breakpoints. */
  hideBackOnDesktop?: boolean;
}

const PageHeader: FC<PageHeaderProps> = ({
  title,
  icon,
  backRoute,
  subtitle,
  hideBackOnDesktop = false,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const showBack = !!backRoute && !(hideBackOnDesktop && isDesktop);

  const handleBack = (): void => {
    if (backRoute) {
      navigate(backRoute);
    }
  };

  return (
    <Box sx={{ backgroundColor: 'primary.dark', px: 3, py: 3 }}>
      <Stack
        direction="row"
        alignItems="center"
        gap={1.5}
        sx={{ mb: subtitle ? 1 : 0 }}
      >
        {showBack && (
          <IconButton
            onClick={handleBack}
            aria-label="Go back"
            size="small"
            sx={{
              backgroundColor: 'common.white',
              color: 'grey.900',
              width: 34,
              height: 34,
              borderRadius: '50%',
              boxShadow: `0px 1px 4px ${alpha(
                theme.palette.text.secondary,
                0.06
              )}`,
              flexShrink: 0,
              '&:hover': { backgroundColor: 'grey.100' },
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 18 }} />
          </IconButton>
        )}
        <Typography
          variant="h4"
          fontWeight={700}
          color="common.white"
          sx={{ fontSize: 28, lineHeight: 1.2 }}
        >
          {title}
        </Typography>
        {icon && (
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              backgroundColor: 'rgba(255,255,255,0.12)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              '& svg, & .MuiSvgIcon-root': {
                fontSize: 24,
                color: 'common.white',
              },
              '& .material-symbols-outlined': {
                fontSize: '24px',
                color: theme.palette.common.white,
              },
            }}
          >
            {icon}
          </Box>
        )}
      </Stack>
      {subtitle && (
        <Typography variant="body2" color="common.white" sx={{ opacity: 0.8 }}>
          {subtitle}
        </Typography>
      )}
    </Box>
  );
};

export default PageHeader;
